import React, { FC, useEffect, useState } from "react";
import { Alert, Button, ButtonGroup, Dropdown, Modal } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormInput, registerObject } from "go-form";
import useFlash from "go-alert/AlertMessage";
import FileUpload from "go-app/components/ImageForm/FileUpload";
import handleError from "go-app/services/errors";
import { ReactComponent as EmptyImageSVG } from "go-component/images/svg/angle-input.svg";
import handleException from "go-core/api/handleException";
import { ApiError } from "go-core/api/types";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { ReactComponent as MoreSVG } from "go-core/images/svg/actions/more.svg";
import { FormDirty } from "go-form/components/FormDirty";
import { adminApi } from "../../../../services/Api/Admin/adminApi";
import { CancelLoanFormProps } from "../../../../services/Api/Admin/adminTypes";
import { organizationApi } from "../../../../services/Api/Organization/organizationApi";
import { partnerApi } from "../../../../services/Api/Partner/partnerApi";
import { FileAttachmentApi, LoanApi } from "../../../../services/Api/types";
import FileDisplay from "../FileUpload/FileDisplay";

interface Props {
	onHide: () => void;
	isShown: boolean;
	isAdminMode: boolean;
	loan: LoanApi;
	partnerId: number | undefined;
	onSuccessfulCancelAction: (loan: LoanApi) => void;
	onSuccessfulGenerateAction: () => void;
}

const CancelLoanModal: FC<Props> = ({
	onHide,
	isShown,
	isAdminMode,
	loan,
	partnerId,
	onSuccessfulCancelAction,
	onSuccessfulGenerateAction,
}) => {
	const { t } = useTranslation("admin");
	const [loading, setLoading] = useState<boolean>(false);
	const [generateTerminationLoading, setGenerateTerminationLoading] = useState<boolean>(false);
	const [attachmentsError, setAttachmentsError] = useState<ApiError | undefined>(undefined);
	const { addFlash, addSuccessFlash } = useFlash();
	const form = useForm<CancelLoanFormProps>({
		criteriaMode: "all",
	});
	const {
		handleSubmit,
		formState,
		formState: { errors },
		setError,
		register,
		control,
		setValue,
		clearErrors,
	} = form;
	const { append, remove, fields } = useFieldArray({
		keyName: "key",
		name: "attachments",
		control,
	});

	useEffect(() => {
		fields.forEach((field, index) => {
			registerFile(field, index);
		});
		setAttachmentsError(undefined);
		clearErrors("attachments");
	}, [fields]);

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		try {
			const params: Record<string, any> = { include: "organization,partner,items,attachments" };
			let res;
			if (isAdminMode) {
				res = await adminApi.cancelLoan(loan.id, data, params);
			} else {
				res = await partnerApi.cancelLoan(Number(partnerId), loan.id, data, params);
			}
			onSuccessfulCancelAction(res.data.data);
			onHide();
			addSuccessFlash(t("lib:common.flash.saved"));
		} catch (err) {
			handleError.form(err, setError, addFlash);
			const exceptionErrors = handleException(err.response?.data ? err.response?.data : err);
			const attachmentsError = exceptionErrors.find((f) => f.field === "attachments");
			if (attachmentsError) setAttachmentsError(attachmentsError);
		}
		setLoading(false);
	});

	const getBase64 = (file: File) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => reject(error);
		});
	};
	const onRemoveFile = async (index: number) => {
		remove(index);
	};

	const registerFile = (file: FileAttachmentApi, index: number) => {
		registerObject(register, `attachments.${index}`, ["name", "content", "id", "url"]);
		setValue(`attachments.${index}`, file);
	};

	const onUpdateFile = async (file: File | undefined) => {
		if (!file) return;

		const fileAsBase64: any = await getBase64(file);
		const createdFile = {
			name: file?.name,
			content: fileAsBase64.split(",")[1],
		};
		append(createdFile);
	};

	const onGenerateTerminationFile = async () => {
		setGenerateTerminationLoading(true);
		try {
			let res;
			if (isAdminMode) {
				res = await adminApi.generateLoanTerminationFile(loan.id);
			} else if (partnerId) {
				res = await partnerApi.generateLoanTerminationFile(Number(partnerId), loan.id);
			} else res = await organizationApi.generateLoanTerminationFile(loan.id);

			const url = window.URL.createObjectURL(new Blob([res.data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `${t("modules.loan.field.loan.title")}_#${loan.reference_id}.pdf`);
			document.body.appendChild(link);
			link.click();
			onSuccessfulGenerateAction();
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setGenerateTerminationLoading(false);
	};

	const alertMessage = loan?.terminated_at
		? t("modules.loan.field.with_date.title", { date: FormatDate(loan?.terminated_at) })
		: t("modules.loan.field.without_date.title");

	return (
		<Modal onHide={onHide} show={isShown}>
			<FormDirty formState={formState} key="cancel-loan-modal-form" onSubmit={onSubmit}>
				<Modal.Header closeButton>
					<Modal.Title>{t("modules.loan.field.loan_termination.title")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{loan.status === "CLOSED" && <Alert variant="warning">{alertMessage}</Alert>}
					{(partnerId || isAdminMode) && (
						<>
							<FormInput
								label={t("common.word.cancellation_reason")}
								name="cancellation_reason"
								errors={errors}
								register={register}
							/>
							<div className="loan-attachments mt-3">
								<div className="d-flex">
									{fields.map((field, index) => {
										return (
											<div className={"file-attachment-wrapper"} key={fields[index].key}>
												<div className={"dropzone d-flex"}>
													<Dropdown
														onClick={(evt) => evt.stopPropagation()}
														as={ButtonGroup}
														className="actions"
													>
														<Dropdown.Toggle draggable={false} as={MoreSVG}>
															{t("common.action.more", { ns: "lib" })}
														</Dropdown.Toggle>
														<Dropdown.Menu>
															<Dropdown.Item onClick={() => onRemoveFile(index)}>
																{t("common.action.remove", { ns: "lib" })}
															</Dropdown.Item>
														</Dropdown.Menu>
													</Dropdown>
													<FileDisplay fileName={field.name} icon={<EmptyImageSVG />} />
												</div>
											</div>
										);
									})}
									<div className={"file-attachment-wrapper"}>
										<FileUpload
											customString={t("modules.loan.action.add_attachment.title")}
											onUpdateFile={(_, file) => onUpdateFile(file)}
										/>
										{attachmentsError && (
											<div className="invalid-feedback d-block">
												{t(`constraints.must_not_be_empty`)}
											</div>
										)}
									</div>
								</div>
							</div>
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					{loan.status === "CLOSED" && (
						<ButtonLoading loading={generateTerminationLoading} onClick={onGenerateTerminationFile}>
							{t("modules.loan.action.generate_termination_file.title")}
						</ButtonLoading>
					)}
					{(isAdminMode || partnerId) && (
						<ButtonLoading loading={loading} type="submit" onSubmit={onSubmit}>
							{t("common.action.save", { ns: "lib" })}
						</ButtonLoading>
					)}
					<Button variant="light" onClick={onHide}>
						{t("common.action.cancel", { ns: "lib" })}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};

export default CancelLoanModal;
